import { useState, useEffect } from 'react';
import { Card, Stack, Container, StackSeparator, Center, Spinner } from '@chakra-ui/react';
import { useParams, useLoaderData } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { ContentType, BlockType, FieldOption, StaticField } from '@/lib/types';
import FormContent from './form-content';
import { STATIC_FIELDS } from '@/lib/constants';

interface ContentTypeField {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  field_options: FieldOption[];
  workspace_id: number;
  content_type_id: number;
  sort_order: number;
  properties: any;
}

export default function EditContent() {
  const fields = useLoaderData() as ContentTypeField[];

  const [rootBlocks, setRootBlocks] = useState<ContentTypeField[]>([]);
  const [contentType, setContentType] = useState<ContentType>();
  const [contentData, setContentData] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);

  const { contentTypeId, id } = useParams<{
    contentTypeId: string;
    id: string;
  }>();

  useEffect(() => {
    async function fetchContentType() {
      if (!contentTypeId) return;

      try {
        const response = await api.get(`content-types/${contentTypeId}`);
        setContentType(response.data.data);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content type',
          duration: 3000,
        });
      }
    }

    fetchContentType();
  }, [contentTypeId]);

  useEffect(() => {
    async function processFieldsAndFetchContent() {
      if (!fields?.length || !contentType || !id) return;
      setIsLoading(true);

      try {
        // Process fields
        const processedBlocks: ContentTypeField[] = [];

        const initialSortedFields = fields.sort(
          (a: ContentTypeField, b: ContentTypeField) => a.sort_order - b.sort_order,
        );

        for (const field of initialSortedFields) {
          // Deep clone the field to avoid mutation issues
          const processedField = { ...field, properties: { ...field.properties } };

          // Process selection fields
          if (processedField.field_type === BlockType.Selection) {
            const sortedOptions = [...processedField.field_options].sort(
              (a: FieldOption, b: FieldOption) => a.sort_order - b.sort_order,
            );

            processedField.properties.options = sortedOptions.map((item: FieldOption) => ({
              label: item.option_label,
              value: item.option_value,
            }));
          }

          // Handle static fields
          if (
            processedField.field_unique_name &&
            STATIC_FIELDS.includes(processedField.field_unique_name as StaticField)
          ) {
            processedField.referenceID = processedField.field_unique_name;
          }

          if (!['s_created_date', 's_is_public'].includes(processedField.field_unique_name)) {
            processedBlocks.push(processedField);
          }
        }

        // Sort fields by sort_order
        const staticFieldsList: ContentTypeField[] = [];
        const sortableFieldsList: ContentTypeField[] = [];

        processedBlocks.forEach((field: ContentTypeField) => {
          if (STATIC_FIELDS.includes(field.field_unique_name as StaticField)) {
            staticFieldsList.push(field);
          } else {
            sortableFieldsList.push(field);
          }
        });

        // Fetch content data
        const contentRes = await api.get(`contents/${id}`);
        if (contentRes.data.data) {
          const content = contentRes.data.data;
          const formattedContent = {
            s_name: { value: content.name },
            s_description: { value: content.description },
            s_icon: { value: content.icon },
            ...content.content,
          };

          setContentData(formattedContent);
        }

        setRootBlocks([...staticFieldsList, ...sortableFieldsList]);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to load content type',
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    }

    processFieldsAndFetchContent();
  }, [contentType, fields, id]);

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header textAlign={'center'}>
        <Card.Title fontSize='3xl'>Edit {contentType && contentType.name}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <Center h='300px'>
            <Spinner size='xl' />
          </Center>
        )}
        <Container maxW={'3xl'}>
          <Stack gap={4} separator={<StackSeparator />}>
            <FormContent rootBlocks={rootBlocks} contentId={id} responses={contentData} />
          </Stack>
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
